import React from "react";
import { useParams, Navigate } from "@/lib/router-compat";
import ContentPage from "@/components/common/ContentPage";
import { BatteryCharging, BatteryFull, CarFront, Factory, Flame, PlugZap, Sun } from "lucide-react";
import QuoteHeroWidget from "@/components/common/QuoteHeroWidget";
import MunicipalityIndex from "@/components/local/MunicipalityIndex";
import ProductCatalogSection from "@/components/products/ProductCatalogSection";
import HowQuotesWork from "@/components/common/HowQuotesWork";
import CategoryCalculatorSection, { calculatorKindBySlug } from "@/components/calculators/CategoryCalculatorSection";
import { localVerticals } from "@/data/localVerticals";
import { buildOfferTitle } from "@/config/seoTitles";
import CompactLinkGrid from "@/components/compact/CompactLinkGrid";
import type { CompactVertical } from "@/data/compact/types";

const COMPACT_VERTICALS: CompactVertical[] = ["laddboxar", "solceller", "varmepumpar", "batterier"];

interface OfferCategory {
  slug: string;
  /** Kort, användarvänlig rubrik som visas på sidan */
  title: string;
  /** Längre variant för SEO-titel */
  seoTitle: string;
  intro: string;
  icon: React.ReactElement;
  leadCategory: string;
  isBusiness?: boolean;
  faq: { q: string; a: string }[];
}

export const offerCategories: OfferCategory[] = [
  {
    slug: "solceller",
    title: "Solceller",
    seoTitle: "Jämför offerter på solceller",
    intro:
      "Sänk elkostnaden med egen solel. Fyll i dina förutsättningar och få kostnadsfria offerter från upp till fyra installatörer.",
    icon: <Sun size={40} />,
    leadCategory: "solceller",
    faq: [
      { q: "Vad kostar solceller?", a: "Priset styrs av takyta, taktyp och anläggningens storlek. Offerterna du får är anpassade efter ditt hus." },
      { q: "Finns det bidrag?", a: "Grönt avdrag ger avdrag direkt på fakturan för både material och arbete. Installatören drar av det i offerten." },
    ],
  },
  {
    slug: "hembatterier",
    title: "Hembatterier",
    seoTitle: "Jämför offerter på hembatterier",
    intro:
      "Ett hembatteri gör att du kan lagra egenproducerad el och utnyttja prisskillnaderna mellan timmarna. Få kostnadsfria offerter från installatörer.",
    icon: <BatteryCharging size={40} />,
    leadCategory: "hembatteri",
    faq: [
      { q: "Vad kostar ett hembatteri?", a: "Priset beror på kapacitet (kWh) och växelriktare. Offerterna du får är anpassade efter ditt hus och din förbrukning." },
      { q: "Kan batteriet kombineras med solceller?", a: "Ja, de flesta system installeras tillsammans med solceller men kan även installeras separat för att kapa dyra timmar." },
    ],
  },
  {
    slug: "varmepumpar",
    title: "Värmepumpar",
    seoTitle: "Jämför offerter på värmepumpar",
    intro:
      "Uppvärmningen står för den största delen av elräkningen i en villa. Få offerter på luft/vatten, bergvärme eller frånluftsvärmepump.",
    icon: <Flame size={40} />,
    leadCategory: "varmepump",
    faq: [
      { q: "Vilken värmepump passar mitt hus?", a: "Det beror på uppvärmningssystem, energibehov och tomt. Installatörerna föreslår rätt typ i sina offerter." },
      { q: "Hur mycket kan jag spara?", a: "Besparingen varierar med nuvarande uppvärmning och elpris. Offerterna innehåller en uppskattad besparing." },
    ],
  },
  {
    slug: "laddboxar",
    title: "Laddboxar",
    seoTitle: "Jämför offerter på laddboxar",
    intro:
      "Ladda elbilen hemma säkert och smart. Få offerter på laddbox inklusive installation av behörig elektriker.",
    icon: <PlugZap size={40} />,
    leadCategory: "laddbox",
    faq: [
      { q: "Ingår installation?", a: "Ja, offerterna omfattar både laddbox och installation av behörig elinstallatör." },
      { q: "Kan laddboxen styras efter spotpris?", a: "Många laddboxar stödjer timstyrning så att bilen laddas under de billigaste timmarna." },
    ],
  },
  {
    slug: "laddstolpar",
    title: "Laddstolpar",
    seoTitle: "Jämför offerter på laddstolpar",
    intro:
      "Laddstolpar för bostadsrättsföreningar, samfälligheter och arbetsplatser. Få offerter på komplett laddinfrastruktur.",
    icon: <CarFront size={40} />,
    leadCategory: "laddstolpe",
    isBusiness: true,
    faq: [
      { q: "Passar det för en BRF?", a: "Ja, leverantörerna projekterar laddstolpar för parkeringar med flera platser inklusive lastbalansering och betallösning." },
      { q: "Finns det bidrag?", a: "Stöd för laddinfrastruktur kan finnas via Naturvårdsverket. Leverantören informerar om vad som gäller vid offerttillfället." },
    ],
  },
  {
    slug: "kommersiella-solceller",
    title: "Kommersiella solceller",
    seoTitle: "Jämför offerter på kommersiella solceller",
    intro:
      "Solcellsanläggningar för företag, lantbruk och fastighetsägare. Få offerter på takanläggningar och markanläggningar.",
    icon: <Factory size={40} />,
    leadCategory: "kommersiella_solceller",
    isBusiness: true,
    faq: [
      { q: "Hur stor anläggning behövs?", a: "Storleken dimensioneras efter er förbrukningsprofil och tillgänglig takyta." },
      { q: "Vad är återbetalningstiden?", a: "Den beror på elpris, investering och egenanvändning. Offerterna innehåller en kalkyl." },
    ],
  },
  {
    slug: "kommersiella-batterier",
    title: "Kommersiella batterier",
    seoTitle: "Jämför offerter på kommersiella batterier",
    intro:
      "Batterilager för företag och fastigheter — effekttoppskapning, stödtjänster och ökad egenanvändning av solel.",
    icon: <BatteryFull size={40} />,
    leadCategory: "kommersiella_batterier",
    isBusiness: true,
    faq: [
      { q: "Kan batteriet tjäna pengar på stödtjänster?", a: "Ja, större batterier kan delta på marknader för frekvensreglering via en aggregator." },
      { q: "Hur stort batteri behövs?", a: "Dimensioneringen utgår från er effektprofil och syftet med lagringen." },
    ],
  },
];

/** Kanonisk sökväg per kategori, t.ex. hembatterier → /batterier. */
export const categoryPaths: Record<string, string> = {
  solceller: "/solceller",
  hembatterier: "/batterier",
  varmepumpar: "/varmepumpar",
  laddboxar: "/laddboxar",
  laddstolpar: "/laddstolpar",
  "kommersiella-solceller": "/kommersiella-solceller",
  "kommersiella-batterier": "/kommersiella-batterier",
};

export const categoryPath = (slug: string) => categoryPaths[slug] ?? `/${slug}`;

interface Props {
  /** Kategorins slug när rutten är statisk, annars läses :slug */
  slug?: string;
}

const OffertKategori = ({ slug: staticSlug }: Props) => {
  const { slug } = useParams();
  const active = staticSlug ?? slug;

  const category = offerCategories.find((c) => c.slug === active);
  if (!category) return <Navigate to="/batterier" replace />;

  const localVertical = localVerticals.find(
    (v) => v.slug === categoryPath(category.slug).slice(1),
  );

  const verticalSlug = categoryPath(category.slug).slice(1);
  const compactVertical = COMPACT_VERTICALS.find((v) => v === verticalSlug);

  return (
    <ContentPage
      key={category.slug}
      contentWidth="contained"
      title={category.title}
      seoTitle={buildOfferTitle(category.title)}
      seoTitleType="raw"
      seoDescription={`${category.seoTitle} från upp till fyra kvalitetssäkrade leverantörer. Kostnadsfritt, utan förpliktelser och med svar inom ett par dagar.`}
      intro={category.intro}
      breadcrumb={[{ label: "Hem", path: "/" }, { label: "Jämför offerter" }, { label: category.title }]}
      icon={category.icon}
      hero={
        <QuoteHeroWidget
          title={category.title}
          subtitle={category.intro}
          icon={category.icon}
          serviceSlug={category.slug}
          defaultCustomerType={category.isBusiness ? "business" : "private"}
        />
      }
      faqTone="surface"
      faq={category.faq}
      relatedLinks={[
        ...offerCategories
          .filter((c) => c.slug !== category.slug)
          .map((c) => ({
            label: c.title,
            to: categoryPath(c.slug),
          })),
      ]}
    >
      <div className="section-bleed -my-12 md:-my-16">
        {calculatorKindBySlug[category.slug] && (
          <section className="bg-section-alt">
            <CategoryCalculatorSection kind={calculatorKindBySlug[category.slug]!} />
          </section>
        )}
        <section className="bg-section-surface">
          <HowQuotesWork serviceLabel={category.title} />
        </section>
        {compactVertical && (
          <section className="bg-section-alt">
            <div className="page-container py-12 md:py-16">
              <CompactLinkGrid
                vertical={compactVertical}
                title={`${category.title} för din situation`}
                intro="Välj det som stämmer in på dig för en kort genomgång av vad du ska tänka på och vad som brukar passa."
              />
            </div>
          </section>
        )}
        <section className="bg-section-surface empty:hidden">
          <ProductCatalogSection serviceSlug={category.slug} />
        </section>
        {localVertical && (
          <section className="bg-section-alt">
            <MunicipalityIndex
              basePath={categoryPath(category.slug)}
              heading={`${localVertical.hubTitle}`}
              description={`Välj din kommun för lokal information om ${localVertical.label} och offerter från installatörer i närområdet.`}
            />
          </section>
        )}
      </div>


    </ContentPage>
  );
};

export default OffertKategori;

